import { CloseOutlined, LoadingOutlined, LogoutOutlined, MailFilled, UserOutlined } from '@ant-design/icons';
import { Button, Card, Col, Collapse, Layout, Modal, Row, Space, Spin } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import calls from '../api/calls';
import { useBreakpoint } from '../utilities/Hooks';
import { ProfileModal } from './ProfileModal';

type DashboardProps = {
  children: React.ReactNode;
};

type MainProps = {
  titleName?: string;
  panels: Array<{ title: string | React.ReactNode | JSX.Element; content: React.ReactNode }>;
  sideActive?: boolean;
};

type SideProps = {
  title: string | React.ReactDOM | JSX.Element;
  onClose: () => void;
  active?: boolean;
  children?: React.ReactNode;
};

type LoaderProps = {
  loading?: boolean;
};

const Dashboard: React.FunctionComponent<DashboardProps> & {
  Main: typeof DashboardMain;
  Side: typeof DashboardSide;
  Loader: typeof DashboardLoader;
} = ({ children }: DashboardProps) => {
  return (
    <Layout className="pg-dashboard-layout">
      <Layout.Content>
        <Row justify="center" align="top" gutter={{ xs: 0, xxl: 24 }}>
          {children}
        </Row>
      </Layout.Content>
    </Layout>
  );
};

const DashboardMain = ({ titleName = '', panels, sideActive = false }: MainProps) => {
  const { t } = useTranslation();
  const [profileVisible, setProfileVisible] = useState(false);

  const confirmLogoutModal = () => {
    Modal.confirm({
      title: t('logoutConfirmTitle'),
      content: t('logoutConfirmDescription'),
      okText: t('yes'),
      cancelText: t('no'),
      onOk: () => calls.auth.logout(),
    });
  };

  function contactInfoModal() {
    Modal.info({
      title: t('emailInfoTitle'),
      content: t('emailInfoDescription'),
      okText: t('ok'),
    });
  }

  const renderInvestorTitle = () => (
    <Space size="small">
      {t('hiName', { name: titleName ?? '' })}
      <UserOutlined onClick={() => setProfileVisible(true)} />
    </Space>
  );

  return (
    <>
      <ProfileModal visible={profileVisible} onCancel={() => setProfileVisible(false)} />
      <Col xs={22} md={20} xl={16} xxl={sideActive ? 6 : 14} className="pg-animated" style={{ marginBottom: 24 }}>
        <Card
          style={{ marginBottom: 24 }}
          className="pg-dashboard-main-card"
          title={renderInvestorTitle()}
          actions={[
            <LogoutOutlined key="logout" onClick={confirmLogoutModal} />,
            <MailFilled key="contact" onClick={contactInfoModal} />,
            <UserOutlined key="profile" onClick={() => setProfileVisible(true)} />,
          ]}>
          <Collapse defaultActiveKey={Array.from(Array(panels.length).keys())}>
            {panels.map((panel, index) => (
              <Collapse.Panel header={panel.title} key={index}>
                {panel.content}
              </Collapse.Panel>
            ))}
          </Collapse>
        </Card>
      </Col>
    </>
  );
};

const DashboardSide: React.FunctionComponent<SideProps> = ({ children, title, onClose, active = false }: SideProps) => {
  return (
    <Col xs={22} md={20} xl={16} xxl={14} data-hidden-width={!active} className="pg-animated">
      {active && (
        <Card title={title} extra={<Button type="text" size="small" icon={<CloseOutlined />} onClick={onClose} />}>
          {children}
        </Card>
      )}
    </Col>
  );
};

const DashboardLoader = ({ loading = false }: LoaderProps) => {
  const breakpoint = useBreakpoint({
    small: 1600,
  });
  return (
    breakpoint === 'small' && (
      <Spin
        className="pg-page-spin"
        size="large"
        spinning={loading}
        indicator={<LoadingOutlined style={{ fontSize: 48, color: 'white' }} spin />}></Spin>
    )
  );
};

Dashboard.Main = DashboardMain;
Dashboard.Side = DashboardSide;
Dashboard.Loader = DashboardLoader;

export { Dashboard };
